import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  MapPin,
  ToggleLeft,
  ToggleRight,
  X,
} from 'lucide-react';
import { Button, Input, useToast, Card } from '@caruso/ui';
import { tablesService } from '@/services/tables.service';

interface Table {
  id: string;
  number: string;
  capacity: number;
  location?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    orders: number;
  };
}

interface TableFormData {
  number: string;
  capacity: number;
  location: string;
  active: boolean;
}

export default function TablesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [formData, setFormData] = useState<TableFormData>({
    number: '',
    capacity: 4,
    location: '',
    active: true,
  });

  const { data: tables, isLoading } = useQuery({
    queryKey: ['tables'],
    queryFn: () => tablesService.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: tablesService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      setShowModal(false);
      resetForm();
      toast({
        title: 'Mesa criada com sucesso!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao criar mesa',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TableFormData> }) =>
      tablesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      setShowModal(false);
      setEditingTable(null);
      resetForm();
      toast({
        title: 'Mesa atualizada com sucesso!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar mesa',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: tablesService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast({
        title: 'Mesa excluída com sucesso!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir mesa',
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setFormData({
      number: '',
      capacity: 4,
      location: '',
      active: true,
    });
  };

  const handleOpenModal = (table?: Table) => {
    if (table) {
      setEditingTable(table);
      setFormData({
        number: table.number,
        capacity: table.capacity,
        location: table.location || '',
        active: table.active,
      });
    } else {
      setEditingTable(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTable(null);
    resetForm();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingTable) {
      updateMutation.mutate({
        id: editingTable.id,
        data: formData,
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta mesa?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleActive = (table: Table) => {
    updateMutation.mutate({
      id: table.id,
      data: { active: !table.active },
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-12 text-brand-ivory/50">
        Carregando...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Mesas</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie as mesas do estabelecimento
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Nova Mesa
        </Button>
      </div>

      {/* Tables Grid */}
      {tables?.length === 0 ? (
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-brand-gold/10 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-brand-gold" />
            </div>
            <h3 className="text-xl font-semibold text-brand-ivory mb-2">
              Nenhuma mesa cadastrada
            </h3>
            <p className="text-brand-ivory/60 mb-6">
              Comece cadastrando a primeira mesa do seu estabelecimento
            </p>
            <Button onClick={() => handleOpenModal()} variant="premium">
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Primeira Mesa
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tables?.map((table: Table) => (
            <motion.div
              key={table.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-serif text-brand-gold">
                        Mesa {table.number}
                      </h3>
                      <div className="flex items-center gap-2 mt-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            table.active
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-brand-ivory/20 text-brand-ivory/60'
                          }`}
                        >
                          {table.active ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleActive(table);
                      }}
                    >
                      {table.active ? (
                        <ToggleRight className="w-5 h-5 text-brand-gold" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-brand-ivory/60" />
                      )}
                    </Button>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-2 text-brand-ivory/60">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">
                        Capacidade: {table.capacity} pessoas
                      </span>
                    </div>
                    {table.location && (
                      <div className="flex items-center gap-2 text-brand-ivory/60">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{table.location}</span>
                      </div>
                    )}
                  </div>

                  {table._count && table._count.orders > 0 && (
                    <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                      <p className="text-sm text-orange-400">
                        {table._count.orders} comanda(s) em aberto
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-4 border-t border-brand-gold/10">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(table);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(table.id);
                      }}
                      disabled={table._count && table._count.orders > 0}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={handleCloseModal}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
            >
              <Card className="w-full max-w-lg bg-brand-ink border-brand-gold/20 pointer-events-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif text-brand-gold">
                      {editingTable ? 'Editar Mesa' : 'Nova Mesa'}
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleCloseModal}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Número da Mesa *
                      </label>
                      <Input
                        type="text"
                        value={formData.number}
                        onChange={(e) =>
                          setFormData({ ...formData, number: e.target.value })
                        }
                        className="bg-brand-ink border-brand-gold/20 text-brand-ivory"
                        placeholder="Ex: 1, A1, VIP1"
                        style={{
                          colorScheme: 'dark',
                          color: 'rgb(244, 241, 233) !important' as any,
                          WebkitTextFillColor: 'rgb(244, 241, 233)',
                        }}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Capacidade *
                      </label>
                      <Input
                        type="number"
                        value={formData.capacity}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            capacity: parseInt(e.target.value) || 0,
                          })
                        }
                        className="bg-brand-ink border-brand-gold/20 text-brand-ivory"
                        placeholder="Número de pessoas"
                        style={{
                          colorScheme: 'dark',
                          color: 'rgb(244, 241, 233) !important' as any,
                          WebkitTextFillColor: 'rgb(244, 241, 233)',
                        }}
                        min="1"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Localização
                      </label>
                      <Input
                        type="text"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData({ ...formData, location: e.target.value })
                        }
                        className="bg-brand-ink border-brand-gold/20 text-brand-ivory"
                        placeholder="Ex: Salão Principal, Varanda, Área VIP"
                        style={{
                          colorScheme: 'dark',
                          color: 'rgb(244, 241, 233) !important' as any,
                          WebkitTextFillColor: 'rgb(244, 241, 233)',
                        }}
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="active"
                        checked={formData.active}
                        onChange={(e) =>
                          setFormData({ ...formData, active: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-brand-gold/20 bg-brand-ink text-brand-gold focus:ring-brand-gold focus:ring-offset-0"
                      />
                      <label
                        htmlFor="active"
                        className="text-sm font-medium text-brand-ivory/80"
                      >
                        Mesa ativa
                      </label>
                    </div>

                    <div className="flex items-center gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={handleCloseModal}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        variant="premium"
                        className="flex-1"
                        disabled={createMutation.isPending || updateMutation.isPending}
                      >
                        {createMutation.isPending || updateMutation.isPending
                          ? 'Salvando...'
                          : 'Salvar'}
                      </Button>
                    </div>
                  </form>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
